import { useEffect, useRef, useCallback } from 'react'
import { X, Download } from 'lucide-react'
import { downloadVideo } from '@/lib/download'
import { Button } from '@/components/ui/button'
import { VideoItem } from './VideoCard'
import { PlyrPlayer } from './PlyrPlayer'

interface VideoPlayerProps {
  video: VideoItem
  onClose: () => void
}

export function VideoPlayer({ video, onClose }: VideoPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  // Close on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // Close on click outside
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === containerRef.current) {
      onClose()
    }
  }, [onClose])

  const handleDownload = async () => {
    if (!video.videoUrl) return
    try {
      await downloadVideo(video.videoUrl, `${video.title || video.prompt || 'video'}.mp4`)
    } catch (error) {
      console.error('Failed to download video:', error)
    }
  }

  if (!video.videoUrl) return null

  return (
    <div
      ref={containerRef}
      onClick={handleBackdropClick}
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div className="relative w-full max-w-5xl">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white transition-colors"
        >
          <X size={24} />
        </button>

        {/* Video container with Plyr */}
        <div className="relative rounded-xl overflow-hidden bg-black shadow-2xl">
          <PlyrPlayer
            src={video.videoUrl}
            poster={video.thumbnailUrl || undefined}
            autoPlay
          />
        </div>

        {/* Video info and actions */}
        <div className="mt-4 flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {video.title && (
              <h2 className="text-lg font-semibold text-white truncate">
                {video.title}
              </h2>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownload}
              className="text-neutral-400 hover:text-white"
            >
              <Download size={16} className="mr-2" />
              Download
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
